<template>
  <div id="app" class="master">
    <transition-group name="auth-fade" appear>
      <div v-if="credentials.access_token" key="app" class="app">
        <Nav class="site-nav" />
        <Header class="site-header" />
        <div class="body">
          <Notifications />
          <div class="page">
            <router-view class="page__body" />
            <Voip class="page__aside" />
          </div>
        </div>
      </div>
    </transition-group>
    <CourseConnectionWebsocket></CourseConnectionWebsocket>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';

import { heartbeat } from '@/services/heartbeat';

import webSocketMixin from '@/mixins/webSocketMixin';

import Header from '@/components/layout/Header';
import Nav from '@/components/layout/Nav';
import Voip from '@/components/voip/Voip';
import Notifications from '@/components/layout/Notifications';
import CourseConnectionWebsocket from '@/components/courseConnection/CourseConnectionWebsocket';

export default {
  components: {
    CourseConnectionWebsocket,
    Header,
    Nav,
    Voip,
    Notifications,
  },

  mixins: [webSocketMixin],

  socket: {
    namespace: '/api/user/',
    skip() {
      return !this.credentials.access_token;
    },
    events: {
      message(event) {
        const data = JSON.parse(event.data);

        switch (data.type) {
          case 'model.create':
          case 'model.change':
            this.setUser(data.instance);
            break;
          case 'model.delete':
            this.removeUser(data.instance);
            break;
        }
      },
    },
  },

  computed: {
    ...mapState('auth', ['credentials']),
  },

  watch: {
    'credentials.access_token': {
      async handler(token) {
        this.stopHeartbeat();
        if (token) {
          await this.initHeartbeat();
          await this.getUsers();
          this.getGroups();
          this.getDirectories();
          this.getAndApplySettings();
          this.getNextCourses();
        }
      },
      immediate: true,
    },
  },

  beforeDestroy() {
    this.stopHeartbeat();
  },

  methods: {
    ...mapActions('users', ['getUsers']),
    ...mapActions('planification', ['getGroups']),
    ...mapActions('directories', ['getDirectories']),
    ...mapActions('settings', ['getAndApplySettings']),
    ...mapActions('courses', ['getNextCourses']),
    ...mapMutations('users', ['setUser', 'removeUser']),

    initHeartbeat() {
      this.heartbeatInterval = setInterval(() => {
        heartbeat();
      }, 10000);
      heartbeat();
    },

    stopHeartbeat() {
      clearInterval(this.heartbeatInterval);
    },

    applySettings() {
      this.applySettings();
    },
  },
};
</script>

<style lang="scss">
.container {
  padding: $container-gutter-width;
  width: 100%;
  max-width: $container-max-width;
}
</style>

<style lang="scss" scoped>
.app,
.auth {
  display: grid;

  height: 100vh;
  min-width: 1024px;
}

.app {
  grid-template-areas: 'nav header' 'nav page';
  grid-template-columns: 80px 1fr;
  grid-template-rows: min-content 1fr;
}

.auth {
  align-items: center;
  justify-content: center;

  background-color: $alt-100;
  background-image: linear-gradient(-50deg, $alt-800 50%, $alt-700 50%);
}

.body {
  display: grid;
  grid-area: page;

  position: relative;
}

.site-nav {
  grid-area: nav;
}

.site-header {
  grid-area: header;
}

.page {
  display: grid;
  grid-template-columns: 1fr auto;

  position: relative;
}

.page__body {
  display: grid;
}

.page__aside {
  width: 500px;
  max-width: 100%;
  z-index: 1;
}

.auth-fade-enter-active,
.auth-fade-leave-active {
  opacity: 1;
  transition: opacity 0.5s cubic-bezier(0.17, 0.67, 0.24, 1);
}

.auth-fade-enter,
.auth-fade-leave-to {
  opacity: 0;

  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}
</style>
